import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { statusColumn } from './sharedColumns';
import { candidateCellColumn } from './CandidateCell';
import RowActionsCell from './RowActionsCell';
import { formatDate } from './tabFormatters';
import PipelineDataTable, { candidateShape } from './PipelineDataTable';
import TabEmptyState from './TabEmptyState';
import { registerTab } from './tabRegistry';

// Secondary screening flow — "Closed" tab (data-stage="closed" in the
// mockup). Covers every terminal outcome the mockup's `closed.kind` can
// take (nonresponsive / noshow / rejected / notinterested, etc.) — the
// "Reason" column is what tells them apart.
//
// NOTE: `closed_reason` / `closed_at` / `closed_by` are best-guess field
// names from the mockup's copy ("Reason" / "Closed on & by" columns) — not
// a confirmed API contract yet.
const ClosedTab = ({
  candidates,
  isLoading,
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
  opportunityId,
  assignmentId,
  requirementLocationId,
  stateMachineKey,
  manualActions,
  onActionSuccess,
  onViewProfile,
}) => {
  const columns = useMemo(
    () => [
      candidateCellColumn,
      {
        key: 'closed_reason',
        header: 'Reason',
        className: 'text-sm',
        tooltip: true,
        render: (_v, row) => row.closed_reason || '-',
      },
      {
        key: 'closed_on_by',
        header: 'Closed on & by',
        className: 'text-sm',
        render: (_v, row) =>
          [formatDate(row.closed_at), row.closed_by]
            .filter(Boolean)
            .join(' · ') || '-',
      },
      statusColumn,
      {
        key: 'row_actions',
        header: '',
        className: 'text-sm',
        render: (_v, row) => (
          <RowActionsCell
            candidateId={row.candidate_id}
            mappingId={row.id}
            status="closed"
            opportunityId={opportunityId}
            assignmentId={assignmentId}
            requirementLocationId={requirementLocationId}
            stateMachineKey={stateMachineKey}
            manualActions={manualActions}
            onActionSuccess={onActionSuccess}
            onViewProfile={onViewProfile}
          />
        ),
      },
    ],
    [
      opportunityId,
      assignmentId,
      requirementLocationId,
      stateMachineKey,
      manualActions,
      onActionSuccess,
      onViewProfile,
    ]
  );

  return (
    <PipelineDataTable
      columns={columns}
      candidates={candidates}
      isLoading={isLoading}
      page={page}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      emptyState={
        <TabEmptyState description="No candidates have been closed out yet." />
      }
    />
  );
};

ClosedTab.propTypes = {
  candidates: PropTypes.arrayOf(candidateShape),
  isLoading: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalPages: PropTypes.number,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requirementLocationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  stateMachineKey: PropTypes.string,
  manualActions: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
  ),
  onActionSuccess: PropTypes.func,
  onViewProfile: PropTypes.func,
};

ClosedTab.defaultProps = {
  candidates: [],
  isLoading: false,
  page: 1,
  pageSize: 10,
  totalPages: 1,
  totalItems: 0,
  onPageChange: () => {},
  onPageSizeChange: () => {},
  opportunityId: undefined,
  assignmentId: undefined,
  requirementLocationId: undefined,
  stateMachineKey: 'secondary',
  manualActions: [],
  onActionSuccess: () => {},
  onViewProfile: () => {},
};

registerTab({
  stateMachineKey: 'secondary',
  state: 'closed',
  component: ClosedTab,
});

export default ClosedTab;
